import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { HoverCard } from '@/components/ui/HoverCard';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { useTrendSuggestions, useRefreshTrends } from '@/hooks/useTrendSuggestions';
import { usePostCreatorStore } from '@/stores/postCreatorStore';
import { useAI } from '@/hooks/useAI';
import { useAIBrandProfiles } from '@/hooks/useAIBrandProfiles';
import { toast } from '@/utils/toast';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { TrendSuggestionItem } from '@/hooks/useTrendSuggestions';

/**
 * Trend Suggestions Section - Trending topics and content ideas
 * Shows AI-generated trending topics in user's industry with actionable insights
 */

/**
 * Parse markdown links from text and return clean text + source links
 * Handles patterns like: ([domain.com](https://...)) and [domain.com](https://...)
 */
function parseSourceLinks(text: string): { clean: string; sources: { label: string; url: string }[] } {
  const sources: { label: string; url: string }[] = [];
  // Match ([label](url)) or [label](url)
  const clean = text.replace(/\s*\(?\[([^\]]+)\]\((https?:\/\/[^)]+)\)\)?/g, (_, label: string, url: string) => {
    // Remove utm params for cleaner display
    const cleanUrl = url.split('?')[0];
    // Avoid duplicate sources
    if (!sources.some((s) => s.url === cleanUrl)) {
      sources.push({ label, url: cleanUrl });
    }
    return '';
  }).replace(/\s+\./g, '.').trim();

  return { clean, sources };
}

const trendStyles: Record<string, { bg: string; text: string; icon: string; topBorder: string; gradient: string }> = {
  'Hot': { bg: 'bg-red-50', text: 'text-red-600', icon: '🔥', topBorder: 'border-t-red-400', gradient: 'from-red-500 to-orange-500' },
  'Rising': { bg: 'bg-orange-50', text: 'text-orange-600', icon: '📈', topBorder: 'border-t-orange-400', gradient: 'from-orange-400 to-amber-400' },
  'Stable': { bg: 'bg-primary-50', text: 'text-primary-600', icon: '📊', topBorder: 'border-t-primary-400', gradient: 'from-primary-400 to-primary-500' },
};

export const TrendSuggestionsSection = () => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useTrendSuggestions();
  const refreshMutation = useRefreshTrends();
  const { defaultProfile } = useAIBrandProfiles();

  // PostCreator store
  const openPostCreator = usePostCreatorStore((s) => s.open);
  const [generatingTrendId, setGeneratingTrendId] = useState<number | null>(null);
  const [showAllTrends, setShowAllTrends] = useState(false);
  const isMobile = useMediaQuery('(max-width: 639px)');

  const visibleTrends = useMemo(() => {
    const items = data?.items || [];
    if (!isMobile || showAllTrends) return items;
    return items.slice(0, 3);
  }, [data?.items, isMobile, showAllTrends]);

  const hiddenCount = (data?.items?.length || 0) - visibleTrends.length;
  const { generateCaption, isGenerating, isErrorHandled } = useAI();

  // Handler for creating post from trend
  const handleCreateFromTrend = async (trend: TrendSuggestionItem) => {
    setGeneratingTrendId(trend.id);

    // Build rich, structured context for the caption AI
    // Strip markdown source links from description — they waste tokens and confuse caption generation
    const cleanDescription = trend.description ? parseSourceLinks(trend.description).clean : '';
    const hashtagsList = trend.hashtags.length > 0 ? trend.hashtags.map(h => `#${h}`).join(' ') : '';

    const topicParts = [
      `TRENDING TOPIC: ${trend.topic}`,
      trend.category && `CATEGORY: ${trend.category}`,
      trend.trendScore && `TREND LEVEL: ${trend.trendScore} (${trend.trendScore === 'Hot' ? 'viral right now, act fast' : trend.trendScore === 'Rising' ? 'gaining momentum this week' : 'steady ongoing conversation'})`,
      cleanDescription && `WHY IT'S TRENDING: ${cleanDescription}`,
      trend.actionTip && `CONTENT ANGLE: ${trend.actionTip}`,
      hashtagsList && `RELEVANT HASHTAGS: ${hashtagsList}`,
    ].filter(Boolean).join('\n');

    const response = await generateCaption({
      topic: topicParts,
      length: 'medium',
      ...(defaultProfile?.id && { brandProfileId: defaultProfile.id }),
    });
    setGeneratingTrendId(null);
    if (!response) {
      if (!isErrorHandled()) toast.error('Failed to generate caption');
      return;
    }
    const caption = hashtagsList ? `${response.content}\n\n${hashtagsList}` : response.content;
    openPostCreator({ initialCaption: caption });
  };

  // No industries set (empty state)
  if (!isLoading && data && !data.hasIndustries) {
    return (
      <div className="text-center py-8 px-4">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 grid place-items-center">
          <Icon name={ICONS.COMPASS} size="md" color="text-gray-400" ariaHidden />
        </div>
        <p className="text-sm font-medium text-gray-600 mb-1">
          Set your industries to get personalized trends
        </p>
        <p className="text-xs text-gray-400 mb-4">
          Go to Settings → Industries to configure
        </p>
        <button
          onClick={() => navigate('/settings?tab=team#industries')}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          Go to Settings →
        </button>
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-5 py-8 text-center">
        <p className="text-sm text-gray-600">{t('dashboard_trends_load_error')}</p>
        <button
          type="button"
          onClick={() => { void refetch(); }}
          className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          {t('common:try_again')}
        </button>
      </div>
    );
  }

  if (!isLoading && (!data || data.items.length === 0)) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-5 py-8 text-center">
        <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-primary-50">
          <Icon name={ICONS.COMPASS} size="sm" color="text-primary-600" ariaHidden />
        </div>
        <p className="text-sm text-gray-600">{t('trends_empty')}</p>
        <button
          type="button"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="mt-3 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {t('dashboard_trends_generate')}
        </button>
      </div>
    );
  }

  // Loading state (skeleton)
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse bg-white rounded-xl border border-gray-100 border-t-[3px] border-t-gray-200 p-4 pb-3">
            <div className="flex items-center justify-between">
              <div className="w-7 h-7 bg-gray-200 rounded-full" />
              <div className="h-4 w-12 bg-gray-100 rounded-full" />
            </div>
            <div className="mt-2.5 h-4 bg-gray-200 rounded w-5/6" />
            <div className="mt-1.5 h-4 bg-gray-200 rounded w-3/5" />
            <div className="mt-1 h-3 bg-gray-100 rounded w-1/4" />
            <div className="mt-2 h-3 bg-gray-100 rounded w-full" />
            <div className="mt-1 h-3 bg-gray-100 rounded w-4/5" />
            <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-gray-100">
              <div className="flex gap-1.5">
                <div className="h-4 w-14 bg-gray-100 rounded" />
                <div className="h-4 w-16 bg-gray-100 rounded" />
              </div>
              <div className="h-6 w-16 bg-gray-100 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Main data view
  return (
    <div className="relative">
      {data?.needsRefresh && data.items.length > 0 && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
          >
            {t('dashboard_trends_refresh')}
          </button>
        </div>
      )}
      {/* Refresh overlay */}
      {refreshMutation.isPending && (
        <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] rounded-xl flex items-center justify-center">
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
            <span className="text-sm font-medium text-primary-600">{t('trends_updating', { defaultValue: 'Updating trends...' })}</span>
          </div>
        </div>
      )}

      {/* Trends grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visibleTrends.map((trend) => {
          const style = trendStyles[trend.trendScore] || trendStyles['Stable'];

          return (
            <HoverCard
              key={trend.id}
              trigger={
                <div
                  className={`flex flex-col bg-white rounded-xl border border-gray-100 border-t-[3px] ${style.topBorder} overflow-hidden transition-all duration-200 hover:shadow-sm hover:border-gray-200 p-4 pb-3 h-full`}
                >
                  {/* Top row: emoji + score badge */}
                  <div className="flex items-center justify-between">
                    <div className={`w-7 h-7 ${style.bg} rounded-full flex items-center justify-center text-sm shrink-0`}>
                      {style.icon}
                    </div>
                    <span className={`px-2 py-0.5 ${style.bg} ${style.text} text-[10px] font-bold uppercase tracking-wide rounded-full shrink-0`}>
                      {trend.trendScore}
                    </span>
                  </div>

                  {/* Topic */}
                  <h4 className="text-sm font-semibold text-gray-900 mt-2.5 line-clamp-2 leading-snug">
                    {trend.topic}
                  </h4>

                  {/* Category */}
                  {trend.category && (
                    <p className="text-xs text-gray-400 mt-1">{trend.category}</p>
                  )}

                  {/* Action tip */}
                  {trend.actionTip && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 italic line-clamp-2 leading-relaxed">
                        &ldquo;{trend.actionTip}&rdquo;
                      </p>
                    </div>
                  )}

                  {/* Footer: divider + hashtags + Create button */}
                  <div className="mt-auto pt-2.5 border-t border-gray-100 flex items-center justify-between">
                    {/* Hashtags (max 2 chips + "+N") */}
                    <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                      {trend.hashtags.length > 0 ? (
                        <>
                          {trend.hashtags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="px-1.5 py-0.5 bg-gray-50 text-gray-500 text-[11px] rounded shrink-0"
                            >
                              #{tag}
                            </span>
                          ))}
                          {trend.hashtags.length > 2 && (
                            <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 text-[11px] rounded shrink-0">
                              +{trend.hashtags.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-[11px] text-gray-300">{t('trends_no_tags', { defaultValue: 'No tags' })}</span>
                      )}
                    </div>

                    {/* Create button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateFromTrend(trend);
                      }}
                      disabled={isGenerating}
                      className="shrink-0 ml-2 flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors disabled:opacity-50"
                    >
                      {generatingTrendId === trend.id ? (
                        <>
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
                          <span>{t('trends_creating', { defaultValue: 'Creating...' })}</span>
                        </>
                      ) : (
                        <>
                          <Icon name={ICONS.EDIT} size="xs" ariaHidden />
                          <span>{t('trends_create_post', { defaultValue: 'Create' })}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              }
              side="right"
              align="start"
              sideOffset={8}
              openDelay={300}
              closeDelay={200}
              className="w-80"
              mobileAnimation="fade"
            >
              {/* Gradient header — full bleed, no padding from parent */}
              <div className={`bg-gradient-to-r ${style.gradient} px-5 pr-14 lg:pr-4 py-4 lg:py-3`}>
                <div className="flex items-center gap-2 mb-2 lg:mb-1.5">
                  <span className="text-base lg:text-xs">{style.icon}</span>
                  <span className="px-2 py-0.5 bg-white/20 text-white text-[10px] font-bold uppercase tracking-wide rounded-full">
                    {trend.trendScore}
                  </span>
                </div>
                <h4 className="text-white text-base lg:text-sm font-semibold leading-snug">{trend.topic}</h4>
                {trend.category && (
                  <span className="text-white/70 text-xs mt-1 block">{trend.category}</span>
                )}
              </div>

              {/* Content area */}
              <div className="p-5 lg:p-4 space-y-4 lg:space-y-3">
                {/* Why It's Trending — only if description exists */}
                {trend.description && (() => {
                  const { clean, sources } = parseSourceLinks(trend.description);
                  return (
                    <div>
                      <p className="text-xs lg:text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('trends_why_trending', { defaultValue: "Why It's Trending" })}</p>
                      <p className="text-sm lg:text-xs text-gray-600 leading-relaxed">{clean}</p>
                      {sources.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <Icon name={ICONS.LINK} size="xs" color="text-gray-300" ariaHidden />
                          {sources.map((source, idx) => (
                            <span key={source.url} className="inline-flex items-center">
                              {idx > 0 && <span className="text-gray-300 mr-1">·</span>}
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs lg:text-[11px] text-primary-500 hover:text-primary-700 hover:underline transition-colors"
                              >
                                {source.label}
                              </a>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Content Idea — only if actionTip exists */}
                {trend.actionTip && (
                  <div>
                    <p className="text-xs lg:text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('trends_content_idea', { defaultValue: 'Content Idea' })}</p>
                    <p className="text-sm lg:text-xs text-gray-600 leading-relaxed">{trend.actionTip}</p>
                  </div>
                )}

                {/* All Hashtags — clickable to copy */}
                {trend.hashtags.length > 0 && (
                  <div>
                    <p className="text-xs lg:text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{t('trends_hashtags', { defaultValue: 'Hashtags' })}</p>
                    <div className="flex flex-wrap gap-1">
                      {trend.hashtags.map((tag) => (
                        <Tooltip key={tag} content={t('common:click_to_copy', { defaultValue: 'Click to copy' })}>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`#${tag}`);
                              toast.success(`Copied #${tag}`);
                            }}
                            className="px-2 py-0.5 bg-gray-50 hover:bg-primary-50 hover:text-primary-600 text-xs lg:text-[11px] text-gray-600 rounded transition-colors"
                          >
                            #{tag}
                          </button>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}

                {/* Create Post CTA */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateFromTrend(trend);
                  }}
                  disabled={isGenerating}
                  className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 lg:py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-primary-600-contrast text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {generatingTrendId === trend.id ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      <span>{t('trends_creating', { defaultValue: 'Creating...' })}</span>
                    </>
                  ) : (
                    <>
                      <Icon name={ICONS.EDIT} size="sm" className="text-white" ariaHidden />
                      <span>{t('trends_create_from_trend', { defaultValue: 'Create Post from Trend' })}</span>
                    </>
                  )}
                </button>
              </div>
            </HoverCard>
          );
        })}
      </div>

      {/* Show more/less button — mobile only */}
      {isMobile && (data?.items?.length || 0) > 3 && (
        <button
          onClick={() => setShowAllTrends(!showAllTrends)}
          className="w-full mt-3 py-2 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          {showAllTrends
            ? t('common:show_less', { defaultValue: 'Show less' })
            : t('common:show_more_count', { defaultValue: 'Show {{count}} more', count: hiddenCount })}
          <span
            className="inline-block transition-transform duration-300"
            style={{ transform: showAllTrends ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            ↓
          </span>
        </button>
      )}
    </div>
  );
};
